import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { getQuestionTitlesByChartType } from 'src/components/Card/CardExporting/chartExporter/questionProcessor';
import { resetChartLegendsState } from 'src/managers/chartManager/stateManagement/legendHandler';
import ConfigurableChartThemeManager from 'src/managers/themeManager/inheritorThemeManager/ConfigurableChartThemeManager';
import { Card, CardMinzd, EvolutiveChart } from '../../../components';
import { annotationStrategyMap } from '../../../stores/actions/annotations';
import {
  fetchEvolutiveChartData, fetchPredictEvolutiveChartData
} from '../../../stores/actions/charts';
import {
  fetchEvolutiveConfigResult, listEvolutiveConfigs, removeEvolutiveConfig
} from '../../../stores/actions/configs';
import { isNullOrEmptyArray } from '../../../util/arrays';
import { removerCaracteresEspeciais } from '../../../util/formatters/stringNormalizer';
import ContextualCardAnnotations from '../../ContextualCardAnnotations';
import ContextualPopupExporting from '../../ContextualPopupExporting';
import { cardsMap } from '../cardsMap';
import { EVOLUTIVE_CARD } from '../cardTypes';
import { exportingTypes } from '../exportingTypes';
import * as Ui from '../styles';
import { graphTypes } from '../types';
import EvolutiveEconfig from './config/EvolutiveConfig';
import { formatEvolutiveData } from './formatData';

/**
 * Valor que deverá ficar em uma configuração de usuário
 */
const DEFAULT_CONFIG_PREDICT = {
  periods: 3,
  initWithPredict: false,
};

/**
 * filters a dataset using the data stored in the given
 * dataField that matches with the given keywords.
 */
// {dataset, datasetFilterField, query}
function filterByKeywords(context) {
  const { dataset, query } = context;
  if (!query) {
    return dataset;
  }
  if (query && query === '') {
    return dataset;
  }

  return dataset.filter((data) => removerCaracteresEspeciais(data)
    .toLowerCase()
    .includes(removerCaracteresEspeciais(query).toLowerCase()));
}

/**
 * @param data dados do grafico evolutivo
 * @param isFullScreen flag indicando se o card do gráfico evolutivo deve ser exibido
 * em fullScreen ou não.
 * @param isSomeoneInFullScreen flag indicando se existe algum card em full screen.
 * @param onFullScreenToggle callback chamado quando existe uma interação com o botão toggle de fullscreen.
 */
function EvolutiveChartCard(props) {
  // title
  const title = 'Gráfico evolutivo';
  // props from parent
  const {
    customerId,
    surveyId,
    surveyTitle,
    questionId,
    questionTitle,
    isFullScreen,
    isSomeoneInFullScreen,
    onFullScreenToggle,
    theme,
  } = props;

  // state
  const [chartData, setChartData] = useState(null);
  const [presets, setPresets] = useState([
    /* {value, label} */
  ]);
  const [evolutiveChartType, setEvolutiveChartType] = useState('line');
  const [filter, setFilter] = useState('');
  const [showConfig, setShowConfig] = useState(false);
  const [selectedEvolutiveConfigId, setSelectedEvolutiveConfigId] = useState(null);
  const [editableConfigId, setEditableConfigId] = useState(null);
  const [isMinimizedScreen, setMinimizeScreen] = useState(false);
  const [showAnnotations, setShowAnnotations] = useState(false);
  const [showExporting, setShowExporting] = useState(false);
  const [workWithPredict, setWorkWithPredict] = useState(
    DEFAULT_CONFIG_PREDICT.initWithPredict,
  );
  // verifica se o preset esta atualizado
  const [isIncomingId, setIsIncomingId] = useState(false);
  // controla o id em caso de remoção
  const [deletedId, setDeletedId] = useState(null);
  // Define a escolha default do primeiro item da lista em caso de troca de questão
  const [resetConfigData, setResetConfigData] = useState(false);

  // redux state
  const dispatch = useDispatch();
  // configurationsState = state.evolutive.configuration.configs
  const { charts: { predictiveChartData: chartPState },
    evolutive: { configuration: { configs: configurationsState, payload: configurationId }},
    evolutive: { result: chartState },
  } = useSelector(
    (state) => state,
  );

  // const chartState = useSelector((state) => state.evolutive.result);
  // const chartPState = useSelector((state) => state.charts.predictiveChartData);
  // const configurationId = useSelector(
  //   (state) => state.evolutive.configuration.payload,
  // );

  // refs
  const cardRef = useRef();
  const chartRef = React.createRef(null);

  // Gerenciador de tema de cores e fontes
  const themeManager = new ConfigurableChartThemeManager(
    selectedEvolutiveConfigId,
    surveyId,
    Array.isArray(chartData) && chartData.length > 0,
    chartRef,
    theme,
    evolutiveChartType,
    EVOLUTIVE_CARD,
  );

  const {
    colorSchemeValue,
    fontsSizes,
    colorPickerColors,
    chartsColors,
    configurationError,
  } = themeManager.getConfig();

  // resetPresets reinicializa os dados da configuração
  const resetPresets = useCallback(() => {
    setPresets([]);
    setSelectedEvolutiveConfigId(null);
  }, []);

  // refreshConfigs atualiza a listagem de configurações
  const refreshConfigs = useCallback(() => {
    if (surveyId && customerId && questionId) {
      resetPresets();
      listEvolutiveConfigs(customerId, surveyId, questionId, dispatch);
    }
  }, [surveyId, customerId, questionId, resetPresets, dispatch]);

  // resetData reinicializa os dados do gráfico
  const resetData = useCallback(() => {
    setChartData([]);
    resetChartLegendsState(EVOLUTIVE_CARD);
  }, []);

  // encaminha os dados do evolutivo para predição
  const recallWithPredict = useCallback(
    (data) => {
      fetchPredictEvolutiveChartData(
        customerId,
        surveyId,
        questionId,
        data,
        DEFAULT_CONFIG_PREDICT.periods,
        dispatch,
      );
    },
    [customerId, questionId, surveyId, dispatch],
  );

  // gancho: quando a triade principal (customerId, surveyId, questionId) alterar
  // efeito: listar todas as configurações para a triade principal
  useEffect(() => {
    refreshConfigs();
  }, [refreshConfigs]);

  // gancho: alteração da listagem de configurações
  // efeito: definir com os dados da store
  useEffect(() => {
    const configKey = `${customerId}_${surveyId}_${questionId}`;
    const configList = configurationsState.payload && configurationsState.payload[configKey];
    if (!configList) {
      resetPresets();
      return;
    }

    if (!configurationsState.running) {
      setPresets(
        configList
          .sort((a, b) => a.value - b.value)
          .map((item) => ({
            label: item.descricao,
            value: item.id,
          })),
      );
      setIsIncomingId(true);
    }
  }, [configurationsState, customerId, questionId, resetPresets, surveyId]);

  // gancho: alteração da listagem de configurações
  // efeito: selecionar o último item selecionado ou 0
  useEffect(() => {
    if (!isNullOrEmptyArray(presets) && cardRef.current && resetConfigData) {
      const scheduledRefresh = () => {
        cardRef.current.selectPresetItemIndex(0);
      };
      setTimeout(scheduledRefresh, 0);
      setResetConfigData(false);
    }
  }, [presets, cardRef, resetConfigData]);

  // gancho: alteração do erro
  // efeito: caso haja um erro, apresentar uma mensagem
  useEffect(() => {
    // get error
    const error = (chartState && chartState.error)
      || (configurationsState && configurationsState.error)
      || (chartPState && chartPState.error);
    // show error if exists
    if (error) {
      toast.error(error.toString());
    }
  }, [chartState, chartPState, configurationsState]);

  // gancho: alteração na identidade da configuração
  // efeito: solicitar a listagem de dados do gráfico a partir do id da condiguração
  useEffect(() => {
    if (selectedEvolutiveConfigId) {
      fetchEvolutiveConfigResult(selectedEvolutiveConfigId, dispatch);
    }
  }, [dispatch, selectedEvolutiveConfigId]);

  // altera a configuração selecionada
  const changeSelectedConfig = useCallback(
    (configId) => {
      const scheduledRefresh = () => {
        cardRef.current.selectPresetItemIndex(configId === -1 ? 0 : configId);
      };
      setTimeout(scheduledRefresh, 0);
      // Verifica se o id da config não foi alterado para forçar uma atualização dos
      // dados neste caso
      if (configId === selectedEvolutiveConfigId) {
        resetPresets();
      }
    },
    [resetPresets, selectedEvolutiveConfigId],
  );

  // gancho: alteração da variavel de controle do id da config selecionada
  // efeito: quando ocorre uma requisição de update ou criação de config o id retornado
  // é setado na config selecionado, caso um item seja removido o valor passado na variável deletedId
  // é responsável por determinar o novo id da configuração
  useEffect(() => {
    if (configurationId.data && !chartState.running && isIncomingId) {
      const index = presets
        .map((pre) => pre.value)
        .indexOf(configurationId.data.id);
      changeSelectedConfig(index);
      setIsIncomingId(false);
    } else if (deletedId && isIncomingId) {
      const index = presets.map((pre) => pre.value).indexOf(deletedId);
      changeSelectedConfig(index);
      setIsIncomingId(false);
    }
  }, [
    changeSelectedConfig,
    chartState.running,
    configurationId.data,
    deletedId,
    isIncomingId,
    presets,
  ]);

  //
  const fetchEvolutiveChartConfigurations = useCallback((data) => {
    const themeColors = theme.colors.charts.colors;
    const themeColorArray = data[0] ? data[0].resultados.map((alternativa, index) => themeColors[index % themeColors.length]) : [];

    fetchEvolutiveChartData(
      surveyId,
      selectedEvolutiveConfigId,
      themeColorArray,
      dispatch,
    );
  }, [dispatch, selectedEvolutiveConfigId, surveyId, theme.colors.charts.colors]);

  // ao modificar o estado dos dados de plotagem
  useEffect(() => {
    // check if has data
    const hasData = chartState
      && chartState.payload
      && selectedEvolutiveConfigId
      && !isNullOrEmptyArray(chartState.payload[selectedEvolutiveConfigId]);
    // set values
    if (hasData) {
      setChartData(chartState.payload[selectedEvolutiveConfigId]);
      // Consistência de que a action só sera chamada uma única vez
      if (JSON.stringify(chartState.payload[selectedEvolutiveConfigId]) !== JSON.stringify(chartData)) {
        fetchEvolutiveChartConfigurations(chartState.payload[selectedEvolutiveConfigId]);
      }
    } else {
      resetData();
    }
    // predict
    if (hasData && workWithPredict) {
      recallWithPredict(chartState.payload[selectedEvolutiveConfigId]);
    }
  }, [
    chartState,
    selectedEvolutiveConfigId,
    resetData,
    recallWithPredict,
    workWithPredict,
    fetchEvolutiveChartConfigurations,
    chartPState,
  ]);

  // ao receber atualização de dados da predição modificar o estado dos dados de plotagem
  /**
   * Função comentada por estar causando inconsitência dos dados na alteração de tema (da aplicapção)
   */
  // useEffect(() => {
  //   // check if has data
  //   const hasData =
  //     chartPState &&
  //     chartPState.payload &&
  //     !isNullOrEmptyArray(chartPState.payload);
  //   // set values
  //   if (hasData) {
  //     setChartData(chartPState.payload);
  //   } else {
  //     resetData();
  //   }
  // }, [chartPState, resetData]);

  // limpar os dados quando a questão for alterada
  useEffect(() => {
    resetData();
    resetPresets();
    setResetConfigData(true);
  }, [questionId, resetData, resetPresets]);

  // handle on open config (modal)
  function handleOpenConfig() {
    setShowConfig(true);
  }

  // handle on close config (modal)
  function handleCloseConfig() {
    setShowConfig(false);
    setEditableConfigId(null);
  }

  // manipula a acionamento da ação de salvar configuração
  function handleSaveConfig() {
    handleCloseConfig();
    refreshConfigs();
  }

  function isFirstConfigInPresetList(configId) {
    return configId === presets[0].value;
  }

  // handle on remove config (card)
  function handleRemoveConfig(deletedConfigId) {
    removeEvolutiveConfig(
      customerId,
      surveyId,
      questionId,
      deletedConfigId,
      dispatch,
    );
    if (deletedConfigId === selectedEvolutiveConfigId) {
      if (isFirstConfigInPresetList(deletedConfigId)) {
        setDeletedId(presets.length > 2 ? presets[1].value : null);
      }
      setDeletedId(presets[0].value);
    }
    resetPresets();
  }

  // handle on edit config (card)
  function handleEditConfig(configId) {
    setEditableConfigId(configId);
    setShowConfig(true);
  }

  // handle configuration selection (card)
  function handleSelectedConfig(configId) {
    setSelectedEvolutiveConfigId(configId);
  }

  // handle full screen toggle
  function handleFullScreenToggle(cardId, isFull) {
    if (onFullScreenToggle) onFullScreenToggle(cardId, isFull);
  }

  // handle change type
  function handleTypeChange(newType) {
    setEvolutiveChartType(newType);
  }

  // handle color scheme type change
  function handleColorSchemeChange(newType) {
    themeManager.updateColorScheme(newType);
  }

  // handle chart font type change
  function handleChartFontChange(newFontsSizes) {
    themeManager.updateFontSize(newFontsSizes);
  }

  // handle change filter value
  function handleChangeFilterValue(text) {
    setFilter(text);
  }

  // manipula a solicitação de minimizar o card
  function handleMinimize() {
    setMinimizeScreen(true);
  }

  // manipula a definição de uso de predição ou não
  function handleWorkWithPredict(withPred) {
    // set work mode
    setWorkWithPredict(withPred);
    // predict
    if (chartData.payload && withPred) {
      recallWithPredict(chartData);
    } else if (selectedEvolutiveConfigId && chartData.payload) {
      fetchEvolutiveConfigResult(selectedEvolutiveConfigId, dispatch);
    }
  }

  function handleCloseAnnotations() {
    setShowAnnotations(false);
  }

  function handleExportingCancel() {
    handleCloseExporting();
  }

  function handleCloseExporting() {
    setShowExporting(false);
  }

  function handleAnnotationsCancel() {
    handleCloseAnnotations();
  }

  function handleOpenAnnotations() {
    setShowAnnotations(true);
  }

  function handleOpenExporting() {
    setShowExporting(true);
  }

  function handleAnnotationsSave() {
    setShowAnnotations(false);
  }

  function handleExportingSave() {
    setShowExporting(false);
  }

  function handleAnnotationsFailure(errorType) {
    const errorStrategy = {
      fetch: () => {
        handleCloseAnnotations();
        const message = 'Houve uma falha ao recuperar sua anotação, tente novamente';
        toast.error(message);
      },
      save: () => {
        const message = 'Não foi possível salvar sua anotação, tente novamente';
        toast.error(message);
      },
    };
    errorStrategy[errorType]();
  }

  // for render configurations
  // menu-structure: {type: [<charttypes>|<configbtn>]}
  // menu-structure: {charttypes = {type, key, selectedIndex, onSelect, data[{value, label}]}}
  // menu-structure: {configbtn = {type, key, onclick}}
  function buildMenu() {
    // result
    const result = [];
    // create chart menu
    const types = graphTypes.get('grafico-evolutivo').type;
    const selectedTypeIndex = types.findIndex(
      (type) => type.value === evolutiveChartType,
    );

    const colorSchemeTypes = graphTypes.get('grafico-evolutivo').colorSchemeType;
    const selectedColorSchemeTypeIndex = colorSchemeTypes.findIndex(
      (type) => type.value === colorSchemeValue,
    );

    const chartFontTypes = graphTypes.get('grafico-evolutivo').chartFontsType;
    const selectedFontTypeIndex = chartFontTypes.findIndex(
      (type) => type.value === fontsSizes,
    );

    const chartTypesOption = {
      type: 'charttypes',
      selectedIndex: selectedTypeIndex,
      data: types,
      onSelect: handleTypeChange,
    };

    const colorSchemeOption = {
      type: 'colorscheme',
      selectedIndex: selectedColorSchemeTypeIndex,
      data: colorSchemeTypes,
      onSelect: handleColorSchemeChange,
    };

    const chartFontsOption = {
      type: 'chartfonts',
      selectedIndex: selectedFontTypeIndex,
      data: chartFontTypes,
      onSelect: handleChartFontChange,
    };

    const annotationsOption = {
      type: 'annotations',
      onClick: handleOpenAnnotations,
    };

    const exportChartOption = {
      type: 'exportChart',
      onClick: handleOpenExporting,
    };

    const configurationOption = {
      type: 'configbtn',
      onClick: handleOpenConfig,
      title: 'Configurar gráfico evolutivo...',
    };

    const toggleButtonOption = {
      type: 'togglebtn',
      checked: workWithPredict,
      onClick: handleWorkWithPredict,
    };

    const options = chartData && chartData.length ? [
      chartTypesOption,
      annotationsOption,
      colorSchemeOption,
      chartFontsOption,
      exportChartOption,
      configurationOption,
      toggleButtonOption,
    ] : [
      chartTypesOption,
      colorSchemeOption,
      chartFontsOption,
      configurationOption,
      toggleButtonOption,
    ];

    result.push(...options.map((option, index) => ({ ...option, key: `#key${index}` })));

    // return
    return result;
  }

  function renderAnnotations() {
    return showAnnotations ? (
      <ContextualCardAnnotations
        strategy={annotationStrategyMap.evolutive}
        customerId={customerId}
        surveyId={surveyId}
        resultKey={selectedEvolutiveConfigId}
        onCancel={handleAnnotationsCancel}
        onSave={handleAnnotationsSave}
        onFailure={handleAnnotationsFailure}
      />
    ) : null;
  }

  function handleExportingTypeChange() {
    // Code here...
  }

  function renderExporting() {
    return showExporting ? (
      <ContextualPopupExporting
        onCancel={handleExportingCancel}
        types={exportingTypes}
        onTypeChange={handleExportingTypeChange}
        theme={theme}
        selectedChartType={evolutiveChartType}
        selectedCardType={EVOLUTIVE_CARD}
        questionLabels={getQuestionTitlesByChartType(EVOLUTIVE_CARD, questionTitle)}
        surveyId={surveyId}
        analyzeId={selectedEvolutiveConfigId}
        customerId={customerId}
      />
    ) : null;
  }

  function renderChartCard() {
    return isMinimizedScreen ? (
      <CardMinzd title={title} onRestore={() => setMinimizeScreen(false)} />
    ) : (
      <Card
        minimizable
        withPresets
        withEditablePreset
        withRemovablePreset
        ref={cardRef}
        cardId={cardsMap.graficoEvolutivo.cardId}
        cardTitle={title}
        cardTooltip={'Compare respostas de perguntas recorrentes em questionários ao longo do tempo'}
        questionTitle={questionTitle}
        isFullScreenMode={isFullScreen}
        menu={buildMenu()}
        withData={Array.isArray(chartData) && chartData.length > 0}
        loading={chartState.running || chartPState.running}
        presets={presets}
        loadingPresets={configurationsState.running}
        onSelectPreset={(items) => handleSelectedConfig(items[0].value)}
        onRemovePreset={(item) => handleRemoveConfig(item.value)}
        onEditPreset={(item) => handleEditConfig(item.value)}
        onChangeFilterValue={handleChangeFilterValue}
        onMaximize={handleFullScreenToggle}
        onMinimize={handleMinimize}
        theme={theme}
      >
        {chartsColors && (
          <EvolutiveChart
            type={evolutiveChartType}
            data={formatEvolutiveData(chartData)}
            updateEvolutiveChartColors={themeManager.updateChartColors}
            colorPickerColors={colorPickerColors}
            colors={themeManager.calculatedChartColorArray}
            chartFontSize={fontsSizes}
            legendsFilter={filter}
            legendsFilterHandler={filterByKeywords}
            theme={theme.colors.charts}
            themeTitle={theme.title}
            error={configurationError}
            chartRef={chartRef}
          />
        )}
      </Card>
    );
  }

  function renderConfig() {
    return showConfig ? (
      <EvolutiveEconfig
        customerId={customerId}
        surveyId={surveyId}
        surveyTitle={surveyTitle}
        questionId={questionId}
        questionTitle={questionTitle}
        configId={editableConfigId}
        isOpen={showConfig}
        onClose={handleCloseConfig}
        onSave={handleSaveConfig}
      />
    ) : null;
  }

  return (
    <Ui.CardContainer
      className="cardContainer"
      cardIsFullScreen={isFullScreen}
      someoneInFullScreen={isSomeoneInFullScreen}
    >
      {/* chart cart */}
      {renderChartCard()}
      {/* modal */}
      {renderConfig()}
      {renderAnnotations()}
      {renderExporting()}
    </Ui.CardContainer>
  );
}

export default EvolutiveChartCard;
